import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Reveal } from "./motion";
import { PRODUCTS, CATEGORIES, img } from "@/data/products";
import { useLang } from "@/i18n/LanguageContext";

const ALL_LABEL = { en: "All", fr: "Tout", de: "Alle", es: "Todos", pt: "Todos", ar: "الكل", zh: "全部", ja: "すべて" };

const Card = ({ p, catLabel }) => (
  <motion.article
    layout
    data-testid="product-card"
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className="group cursor-default"
  >
    <div className="relative overflow-hidden aspect-[4/3] bg-vw-bg3">
      <img
        src={img(p.img)}
        alt={p.name}
        loading="lazy"
        className="h-full w-full object-cover grayscale-[15%] transition-transform duration-700 ease-out group-hover:scale-[1.06]"
      />
      <div className="absolute inset-0 ring-1 ring-inset ring-black/5" />
      <span className="absolute top-3 left-3 bg-white/90 backdrop-blur px-2 py-1 font-mono text-[0.65rem] text-vw-green">
        {p.code}
      </span>
    </div>
    <div className="mt-4 flex items-start justify-between gap-3 border-b border-vw-silver pb-4">
      <div>
        <div className="vw-eyebrow text-vw-ink2/70 mb-1">{catLabel}</div>
        <h3 className="font-serif text-xl md:text-2xl text-vw-ink leading-tight">{p.name}</h3>
      </div>
      <ArrowUpRight
        size={18}
        className="mt-1 text-vw-ink2 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
      />
    </div>
  </motion.article>
);

export const ProductExplorer = () => {
  const { t, lang } = useLang();
  const [active, setActive] = useState("All");
  const [query, setQuery] = useState("");
  const filters = ["All", ...CATEGORIES];
  const catLabel = (c) => (c === "All" ? ALL_LABEL[lang] || "All" : t.cats[c] || c);

  const match = (p) => {
    const q = query.trim().toLowerCase();
    return (
      !q ||
      p.name.toLowerCase().includes(q) ||
      p.code.toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q)
    );
  };

  const groups = useMemo(() => {
    const cats = active === "All" ? CATEGORIES : [active];
    return cats
      .map((c) => ({ category: c, items: PRODUCTS.filter((p) => p.category === c && match(p)) }))
      .filter((g) => g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, query]);

  const total = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <section id="products" className="py-24 md:py-40 bg-vw-bg2 border-y border-vw-silver">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <Reveal className="mb-16 md:mb-24 max-w-3xl">
          <div className="vw-eyebrow text-vw-green mb-6">{t.products.eyebrow}</div>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight text-vw-ink">
            {t.products.pre}
            <span className="italic font-light text-vw-green">{t.products.em}</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          {/* Sticky filters */}
          <div className="md:col-span-3">
            <div className="md:sticky md:top-28">
              <div className="relative mb-8">
                <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-vw-ink2" />
                <Input
                  data-testid="product-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t.products.search}
                  className="pl-7 border-0 border-b border-vw-silver rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-vw-green font-mono text-sm placeholder:text-vw-ink2/60"
                />
              </div>
              <div className="flex flex-col items-start gap-1">
                {filters.map((f) => (
                  <button
                    key={f}
                    data-testid={`filter-${f.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                    onClick={() => setActive(f)}
                    className={`group relative py-2 text-left vw-eyebrow transition-colors duration-300 ${
                      active === f ? "text-vw-green" : "text-vw-ink2 hover:text-vw-ink"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <span
                        className={`h-px bg-vw-green transition-all duration-500 ${
                          active === f ? "w-6" : "w-0 group-hover:w-3"
                        }`}
                      />
                      {catLabel(f)}
                    </span>
                  </button>
                ))}
              </div>
              <div className="mt-8 font-mono text-xs text-vw-ink2">
                {String(total).padStart(2, "0")} / {PRODUCTS.length} {t.products.label}
              </div>
            </div>
          </div>

          {/* Grouped product grid */}
          <div className="md:col-span-9 space-y-20">
            <AnimatePresence mode="popLayout">
              {groups.map((g) => (
                <motion.div layout key={g.category} data-testid={`group-${g.category.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                  <div className="flex items-baseline justify-between border-b border-vw-green/20 pb-4 mb-10">
                    <h3 className="font-serif text-2xl md:text-3xl text-vw-green italic font-light">
                      {catLabel(g.category)}
                    </h3>
                    <span className="font-mono text-xs text-vw-ink2">
                      {String(g.items.length).padStart(2, "0")}
                    </span>
                  </div>
                  <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                    <AnimatePresence mode="popLayout">
                      {g.items.map((p) => (
                        <Card key={p.category + p.code} p={p} catLabel={catLabel(p.category)} />
                      ))}
                    </AnimatePresence>
                  </motion.div>
                </motion.div>
              ))}
            </AnimatePresence>

            {total === 0 && (
              <div data-testid="no-results" className="py-20 text-center font-serif text-2xl text-vw-ink2">
                {t.products.noResults ? t.products.noResults : `No products match “${query}”.`}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
